import { findAll, findWhere } from "../../../../utils/db_functions"

const INPUT_FETCH_CHUNK_SIZE = 500

const PRODUCT_COMPONENT_FIELDS = [
    { id: "finishing_sheet", finishingId: "sheet_option_finishing" },
    { id: "finishing_sheet_inactive", finishingId: "sheet_option_finishing" },
    { id: "finishing_marco", finishingId: "marco_option_finishing" },
    { id: "finishing_marco_passagem", finishingId: "marco_passagem_option_finishing" },
    { id: "finishing_aliz", finishingId: "fixed_aliz_option_finishing" },
    { id: "finishing_aliz_adjustable", finishingId: "adjustable_aliz_option_finishing" },
    { id: "conj_fechadura" },
    { id: "hardware_door_lock" },
    { id: "hardware_puller" },
    { id: "hardware_door_handle" },
    { id: "hardware_bar_anti_panic" },
    { id: "hardware_aerial_spring" },
    { id: "hardware_accessories" },
    { id: "hardware_accessories_02" },
    { id: "hardware_accessories_03" },
    { id: "accessory" },
]

const getComponentValue = (component, key) => (
    component.get ? component.get(key) : component[key]
)

const extractSpecificationIdsFromRows = (rows) => (
    (rows || []).map((row) => row.specification).filter(Boolean)
)

const extractSpecificationIdsFromComponent = (component, finishingId = null) => {
    if (!component) return []

    const specificationIds = []
    for (const inputGroup of getComponentValue(component, "descriptions") || []) {
        specificationIds.push(...extractSpecificationIdsFromRows(inputGroup.rows))
    }

    if (finishingId) {
        const finishingInputGroup = (getComponentValue(component, "finishing_options_inputs") || [])
            .find((group) => group.finishing === finishingId)
        if (finishingInputGroup) {
            specificationIds.push(...extractSpecificationIdsFromRows(finishingInputGroup.rows))
        }
    }

    return specificationIds
}

const getVinculatedComponentId = (component) => {
    if (!component) return null
    const vinculated = getComponentValue(component, "vinculated_component")
    if (!vinculated) return null
    return typeof vinculated === "object" ? (vinculated.id || vinculated.objectId) : vinculated
}

const collectComponentIdsFromProductBlocks = (productBlocks) => {
    const componentIds = []
    for (const block of productBlocks || []) {
        for (const product of block.products || []) {
            for (const field of PRODUCT_COMPONENT_FIELDS) {
                if (product[field.id]) componentIds.push(product[field.id])
            }
            for (const wallPanel of product.wall_panel || []) {
                if (wallPanel.panel_wall_module) componentIds.push(wallPanel.panel_wall_module)
            }
        }
    }
    return [...new Set(componentIds)]
}

const appendComponentSpecificationIds = (specificationIds, component, finishingId, componentsById) => {
    specificationIds.push(...extractSpecificationIdsFromComponent(component, finishingId))
    const vinculatedId = getVinculatedComponentId(component)
    if (vinculatedId && componentsById[vinculatedId]) {
        specificationIds.push(...extractSpecificationIdsFromComponent(componentsById[vinculatedId]))
    }
}

const collectSpecificationIdsFromProduct = (product, kitsById, componentsById) => {
    const specificationIds = []

    for (const field of PRODUCT_COMPONENT_FIELDS) {
        const componentId = product[field.id]
        if (!componentId) continue
        appendComponentSpecificationIds(
            specificationIds,
            componentsById[componentId],
            field.finishingId ? product[field.finishingId] : null,
            componentsById
        )
    }

    for (const wallPanel of product.wall_panel || []) {
        appendComponentSpecificationIds(
            specificationIds,
            componentsById[wallPanel.panel_wall_module],
            null,
            componentsById
        )
    }

    const kit = product.product_code ? kitsById[product.product_code] : null
    if (kit) {
        specificationIds.push(...extractSpecificationIdsFromComponent(kit.get("comp_mounting")))
        specificationIds.push(...extractSpecificationIdsFromComponent(kit.get("instalation_kit")))
    }

    return specificationIds
}

const fetchByObjectIds = async (module, objectIds, includes = null, fieldToSort = null) => {
    const uniqueIds = [...new Set(objectIds.filter(Boolean))]
    if (!uniqueIds.length) return []

    const results = []
    for (let index = 0; index < uniqueIds.length; index += INPUT_FETCH_CHUNK_SIZE) {
        const chunk = uniqueIds.slice(index, index + INPUT_FETCH_CHUNK_SIZE)
        const found = await findWhere(
            module,
            "objectId",
            chunk,
            includes,
            null,
            fieldToSort,
            null,
            chunk.length
        )
        results.push(...found)
    }
    return results
}

const indexById = (items) => items.reduce((accumulator, item) => {
    accumulator[item.id] = item
    return accumulator
}, {})

const loadInputsAndFormulasForProductBlocks = async (productBlocks) => {
    let components = await fetchByObjectIds(
        "ComponenteKit",
        collectComponentIdsFromProductBlocks(productBlocks),
        ["unity_value_hh"]
    )

    const missingVinculatedIds = components
        .map((component) => getVinculatedComponentId(component))
        .filter((id) => id && !components.some((component) => component.id === id))

    if (missingVinculatedIds.length) {
        components = [...components, ...await fetchByObjectIds("ComponenteKit", missingVinculatedIds, ["unity_value_hh"])]
    }

    const componentsById = indexById(components)
    const productCodes = [
        ...new Set(
            (productBlocks || [])
                .flatMap((block) => (block.products || []).map((product) => product.product_code))
                .filter(Boolean)
        ),
    ]
    const kits = await fetchByObjectIds("Kit", productCodes, ["comp_mounting", "instalation_kit"])
    const kitsById = indexById(kits)

    const specificationIds = (productBlocks || []).flatMap((block) =>
        (block.products || []).flatMap((product) =>
            collectSpecificationIdsFromProduct(product, kitsById, componentsById)
        )
    )

    const [inputs, formulas] = await Promise.all([
        fetchByObjectIds("Input", specificationIds, null, "code"),
        findAll("ConvertionConfigs"),
    ])

    return { inputs, formulas, kits, componentsKits: components }
}

export {
    extractSpecificationIdsFromRows,
    extractSpecificationIdsFromComponent,
    collectComponentIdsFromProductBlocks,
    loadInputsAndFormulasForProductBlocks,
}
